import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { Box } from '@mui/material';

import QrCodeIcon from '~/assets/icons/svgs/qr-code.svg?react';
import { Button } from '~/components/ui/button';
import { Text } from '~/components/ui/text';
import { QRCODE_DYNAMIC_MICROSITE_TYPE, QRCODE_STATIC_TYPE } from '~/constants/qr-code';
import { PATH } from '~/constants/route-path';
import { useAppDispatch } from '~/hooks/useRedux';
import { stepperSlice } from '~/store/reducers/stepepr';
import useBuilderStore from '~/store/useBuilderStore';
import { COLORS } from '~/styles/colors';
import { DynamicQrCodeType, StaticQrCodeType } from '~/types/builder';

const CONTENT_MAP = {
  [QRCODE_DYNAMIC_MICROSITE_TYPE.WEBSITE]: {
    title: 'createQrStep1:items.b1.name',
    description: 'builder:linkToWebsite',
  },
  [QRCODE_DYNAMIC_MICROSITE_TYPE.PDF]: {
    title: 'createQrStep1:items.b2.name',
    description: 'builder:showPDFFile',
  },
  [QRCODE_DYNAMIC_MICROSITE_TYPE.LINKS]: {
    title: 'createQrStep1:items.b3.name',
    description: 'builder:shareLinks',
  },
  [QRCODE_DYNAMIC_MICROSITE_TYPE.APP]: {
    title: 'createQrStep1:items.b4.name',
    description: 'createQrStep1:items.b4.subName',
  },
  [QRCODE_DYNAMIC_MICROSITE_TYPE.IMAGES]: {
    title: 'createQrStep1:items.b5.name',
    description: 'builder:shareDifferentImage',
  },
  [QRCODE_DYNAMIC_MICROSITE_TYPE.VCARD_PLUS]: {
    title: 'createQrStep1:items.b6.name',
    description: 'builder:digitalBusinessCard',
  },
  [QRCODE_DYNAMIC_MICROSITE_TYPE.SOCIAL_MEDIA]: {
    title: 'createQrStep1:items.b7.name',
    description: 'builder:yourSocialChannels',
  },
  [QRCODE_DYNAMIC_MICROSITE_TYPE.COUPON]: {
    title: 'createQrStep1:items.b8.name',
    description: 'createQrStep1:items.b8.subName',
  },
  [QRCODE_STATIC_TYPE.URL]: {
    title: 'createQrStep1:items.c1.name',
    description: 'createQrStep1:items.c1.subName',
  },
  [QRCODE_STATIC_TYPE.TEXT]: {
    title: 'createQrStep1:items.c2.name',
    description: 'createQrStep1:items.c2.subName',
  },
  [QRCODE_STATIC_TYPE.WIFI]: {
    title: 'createQrStep1:items.c3.name',
    description: 'createQrStep1:items.c3.subName',
  },
  [QRCODE_STATIC_TYPE.EMAIL]: {
    title: 'createQrStep1:items.c4.name',
    description: 'createQrStep1:items.c4.subName',
  },
  [QRCODE_STATIC_TYPE.VCARD]: {
    title: 'createQrStep1:items.c5.name',
    description: 'createQrStep1:items.c5.subName',
  },
  [QRCODE_STATIC_TYPE.SMS]: {
    title: 'createQrStep1:items.c6.name',
    description: 'createQrStep1:items.c6.subName',
  },
  [QRCODE_STATIC_TYPE.WHATS_APP]: {
    title: 'createQrStep1:items.c7.name',
    description: 'createQrStep1:items.c7.subName',
  },
};

const stepperActions = stepperSlice.actions;

type QrTypeTopCardProps = {
  type: DynamicQrCodeType | StaticQrCodeType;
};

export default function QrTypeTopCard({ type }: QrTypeTopCardProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const styles = useStyles();

  const { activeStep } = useBuilderStore();

  const content = CONTENT_MAP[type];

  const handleChangeQr = () => {
    if (activeStep === 1) {
      dispatch(stepperActions.setRedirectRoute(PATH.QR.CREATE));
    }
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.left}>
        <Box sx={styles.iconContainer}>
          <QrCodeIcon width={34} height={34} />
        </Box>
        <Box sx={styles.texts}>
          <Text variant="h3" sx={{ lineHeight: '24px' }}>
            {t('builder:qrType')}: {t(content.title)}
          </Text>
          <Text variant="input15" sx={{ lineHeight: '20px', color: COLORS.grey400 }}>
            {t(content.description)}
          </Text>
        </Box>
      </Box>
      <Button color={COLORS.textMain} type="text" sx={styles.button} disableRipple onClick={handleChangeQr}>
        {t('builder:change')}
      </Button>
    </Box>
  );
}

function useStyles() {
  return {
    container: css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      background-color: ${COLORS.white};
      box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.05);
      border-radius: 20px;
      padding: 16px;
      margin-bottom: 6px;
    `,
    left: css`
      display: flex;
      align-items: center;
      gap: 12px;
    `,
    iconContainer: css`
      padding: 6px;
      border-radius: 12px;
      background-color: ${COLORS.blue50};
      height: 46px;
    `,
    texts: css`
      display: flex;
      flex-direction: column;
      gap: 2px;
    `,
    button: css`
      text-transform: none;
      color: ${COLORS.textMain};
      text-decoration: underline;
      font-size: 15px;
      font-weight: 500;

      &:hover {
        background-color: transparent;
        text-decoration: underline;
      }
    `,
  };
}
